/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import {Column} from 'react-table'
import Swal from 'sweetalert2'
import {KTCard, KTSVG} from '../../../../../../../../_metronic/helpers'
import {
  KTable,
  KTableHeader,
  KTableLoading,
  KTableSelectionCell,
} from '../../../../../../components/UI'
import {KTableProvider, useKTable} from '../../../../../../contexts/KTableProvider'
import {useLimpieza} from '../../../../../../hooks/useLimpieza'
import {useKhipu} from '../../../../../../hooks/useKhipu'
import {CREATION_CLEANING_ACTIVITIES_SERVICES} from '../../../../services/creation-cleaning-activities.service'
import {ModalAddActivities} from '../modal/add-activities'
import {ShowSnackBar} from '../../../../../../util/functions'
import {SCHEDULE_ACTIVITY_SERVICES} from '../../../../services/schedule-activity.service'
import {CLEANING_SCHEDULE_SERVICES} from '../../../../services'
import {useParameters} from '../../../../hooks/central/useParameters'
import {CleaningActivitiesColumnModel} from '../../../../model/creationCleaningActivities.model'
import {ModalViewQR} from '../../CreationCleaningActivititesWrapper/modal/ModalViewQr'

interface Props {
  setShow: Dispatch<SetStateAction<boolean>>
  ServiceSchedulingId: string
  CampamentoId: string
}
export interface TableActivitiesByScheduleRef {
  reload: () => void
}

const TableActivitiesBySchedule = forwardRef<TableActivitiesByScheduleRef, Props>(
  ({setShow, ServiceSchedulingId, CampamentoId}, ref) => {
    const {
      data,
      setData,
      setPagination,
      loading,
      setLoading,
      pagination,
      selected,
      updateTableDetail,
      setUpdateTableDetail,
      clearTableDetail,
      setClearTableDetail,
      clearSelected,
    } = useKTable()
    const {currentTabItem, changeCurrentTabItem} = useLimpieza()
    const {searchValuesDetail, changeSearchValuesDetail} = useKhipu()
    const [moduloOptions, setModuloOptions] = useState<{value: string; label: string}[]>([])
    const [showModalViewQR, setShowModalViewQR] = useState<boolean>(false)
    const [cleaningActivitySelected, setCleaningActivitySelected] =
      useState<CleaningActivitiesColumnModel>()
    const {typeActions, getTypeActions} = useParameters()
    const moduloIdsFilter = useMemo(() => {
      const m = (searchValuesDetail as any)?.ModuloIds
      return Array.isArray(m) ? m : []
    }, [searchValuesDetail])
    const activityFilterOptions = useMemo(() => {
      const uniqueActivities = new Map<string, {value: string; label: string}>()
      data.forEach((item: any) => {
        if (item?.ActividadId && item?.Actividad && !uniqueActivities.has(item.ActividadId)) {
          uniqueActivities.set(item.ActividadId, {
            value: item.ActividadId,
            label: item.Actividad,
          })
        }
      })
      return Array.from(uniqueActivities.values())
    }, [data])

    const buildFilters = (overrideLstModulos?: string[]) => {
      const sv = searchValuesDetail as any
      const modulos =
        overrideLstModulos !== undefined
          ? overrideLstModulos
          : Array.isArray(sv.ModuloIds)
          ? sv.ModuloIds
          : []
      const {ActividadIds, ModuloIds, lstModulos, ...restSearchValues} = sv
      return {
        ...restSearchValues,
        ...(Array.isArray(ActividadIds) && ActividadIds.length > 0
          ? {ActividadId: ActividadIds.join(',')}
          : {}),
        ...(Array.isArray(modulos) && modulos.length > 0 ? {lstModulos: modulos} : {}),
      }
    }

    const getActivitiesByScheduleList = async (
      pageNumber?: number,
      overrideLstModulos?: string[],
    ) => {
      const page = pageNumber ?? pagination.currentPage
      try {
        setLoading(true)
        const {data: response} =
          await CREATION_CLEANING_ACTIVITIES_SERVICES.getByServiceSchedulingId({
            pageNumber: page,
            size: pagination.perPage,
            CampamentoId,
            ServiceSchedulingId,
            Disponibles: false,
            ...buildFilters(overrideLstModulos),
          })
        if (!response.error) {
          setData(response.data.Registros as any)
          const modulos = Array.isArray(response.data?.Modulos) ? response.data.Modulos : []
          const mappedModulos = modulos
            .filter((item: any) => item?.ModuloId && item?.Modulo)
            .map((item: any) => ({
              value: item.ModuloId,
              label: item.Modulo,
            }))
          setModuloOptions(mappedModulos)
          setPagination({
            ...pagination,
            totalResults: response.total,
          })
        }
        setLoading(false)
      } catch (error) {
        setLoading(false)
      }
    }

    useEffect(() => {
      const limpioModulos = clearTableDetail
      if (currentTabItem.loadingTable || updateTableDetail) {
        clearSelected()
        if (clearTableDetail) {
          const nextSearchValues: any = {...(searchValuesDetail as any)}
          delete nextSearchValues.ModuloIds
          delete nextSearchValues.lstModulos
          changeSearchValuesDetail(nextSearchValues)
        }
        getActivitiesByScheduleList(
          pagination.currentPage,
          limpioModulos && updateTableDetail ? [] : undefined,
        )
        setUpdateTableDetail(false)
        setClearTableDetail(false)
        changeCurrentTabItem({...currentTabItem, loadingTable: false})
      } else {
        getActivitiesByScheduleList()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.currentPage, pagination.perPage, currentTabItem.loadingTable, updateTableDetail])

    const handleViewQR = (row: CleaningActivitiesColumnModel) => {
      setCleaningActivitySelected(row)
      setShowModalViewQR(true)
      // console.log("VIEW ?=> ", row)
    }

    const columns: Column<any>[] = useMemo(
      () => [
        {
          Header: (props) => <KTableHeader tableProps={props} showSelect={true} size='50px' />,
          id: 'selection',
          Cell: ({...props}) => <KTableSelectionCell id={props.data[props.row.index].Id} />,
          width: 50,
        },
        {
          Header: (props) => (
            <KTableHeader
              tableProps={props}
              title='Nro de Órden'
              name={'NroOrden'}
              searcheable
              fromDetail
              size='80px'
            />
          ),
          accessor: 'NroOrden',
          width: 80,
        },
        {
          Header: (props) => (
            <KTableHeader
              tableProps={props}
              title='Actividad de Limpieza'
              name='Actividad'
              searcheable
              fromDetail
              size='250px'
            />
          ),
          accessor: 'Actividad',
          width: 250,
        },
        {
          Header: (props) => (
            <KTableHeader
              tableProps={props}
              title='Tiempo Promedio'
              name={'MinutosPromedio'}
              searcheable
              number
              size='50px'
              fromDetail
            />
          ),
          accessor: 'MinutosPromedio',
          width: 50,
        },
        {
          Header: (props) => (
            <KTableHeader
              tableProps={props}
              title='Tipo de Acción'
              name={'TipoAccionId'}
              selecteable
              data={typeActions}
              fromDetail
              size='150px'
            />
          ),
          accessor: 'TipoAccion',
          width: 150,
        },
        {
          Header: (props) => (
            <KTableHeader
              tableProps={props}
              title='Módulo'
              name={'ModuloIds'}
              multiSelecteable
              data={moduloOptions}
              fromDetail
              size='150px'
            />
          ),
          accessor: 'Modulo',
          width: 150,
        },
        {
          Header: (props) => (
            <KTableHeader
              tableProps={props}
              title='Habitación'
              name={'CodigoHabitacion'}
              searcheable
              fromDetail
              size='150px'
            />
          ),
          accessor: 'Habitacion',
          width: 150,
        },
        {
          Header: (props) => <KTableHeader tableProps={props} title='Vér QR' size='50px' />,
          id: 'actions',
          Cell: ({...props}) => (
            <div
              className='d-flex justify-content-center flex-shrink-0'
              onClick={() => handleViewQR(props.row.original)}
            >
              <a href='#' className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'>
                <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-3' />
              </a>
            </div>
          ),
          width: 50,
        },
      ],
      [selected, activityFilterOptions, moduloOptions, typeActions],
    )

    useEffect(() => {
      getTypeActions(
        {pageNumber: 1, size: 200, tableName: 'TIPOACCION'},
        {},
        {idKey: 'id', labelKey: 'nombreCorto'},
      )
    }, [])

    const handleDelete = async () => {
      if (selected.length < 1 && moduloIdsFilter.length < 1) {
        ShowSnackBar('Seleccione filas o módulos para eliminar', 'error')
        return
      }
      try {
        Swal.fire({
          title: '¿Estás seguro?',
          text: 'Al eliminar no podrás revertir esta acción',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Si, eliminar',
          confirmButtonColor: 'var(--kt-primary)',
          cancelButtonText: 'No',
          showLoaderOnConfirm: true,
          allowOutsideClick: () => !Swal.isLoading(),
        })
          .then(async (result) => {
            if (result.isConfirmed) {
              setLoading(true)
              const {data: response} = await SCHEDULE_ACTIVITY_SERVICES.deleteActivitySettings({
                ServiceSchedulingId: [ServiceSchedulingId],
                ActividadConfiguracionId: selected.map((x) => x.toString()),
                ...(moduloIdsFilter.length > 0 ? {LstModulosId: moduloIdsFilter} : {}),
              })
              if (!response.error) {
                ShowSnackBar('Se eliminó satisfactoriamente')
                const nextSearchValues: any = {...(searchValuesDetail as any)}
                delete nextSearchValues.ModuloIds
                delete nextSearchValues.lstModulos
                changeSearchValuesDetail(nextSearchValues)
                clearSelected()
                getActivitiesByScheduleList(1, [])
              } else {
                ShowSnackBar(response.message, 'error')
              }
              // getSchedulesByActivityList()
              setLoading(false)
              Swal.close()
            } else if (result.isDenied) {
              //Swal.fire('Changes are not saved', '', 'info')
            }
          })
          .catch((error) => {
            setLoading(false)
            ShowSnackBar('Error al eliminar la actividad', 'error')
          })
      } catch (error) {
        ShowSnackBar('Error al eliminar la actividad', 'error')
      }
    }

    const handleNew = () => {
      setShow(true)
    }

    useImperativeHandle(ref, () => ({
      reload: getActivitiesByScheduleList,
    }))

    return (
      <>
        <KTCard shadow className='m-5'>
          <div className='d-flex justify-content-end align-items-center p-2 me-5'>
            <div className='d-flex align-items-center'>
              {loading && (
                <span
                  className='spinner-border spinner-border-sm text-primary me-3'
                  role='status'
                />
              )}
              <button
                type='button'
                className='btn btn-success me-3'
                onClick={handleNew}
                disabled={loading}
              >
                Agregar Actividad
              </button>
              <button
                type='button'
                className='btn btn-primary me-3'
                disabled={loading || (selected.length < 1 && moduloIdsFilter.length < 1)}
                onClick={handleDelete}
              >
                Eliminar
              </button>
            </div>
          </div>
          <KTable data={data} columns={columns as any} showPageSizeSelector fromDetail />
          {loading ? <KTableLoading title='Cargando...' /> : null}
          {showModalViewQR && cleaningActivitySelected && (
            <ModalViewQR
              show={showModalViewQR}
              setShow={setShowModalViewQR}
              cleaningActivity={cleaningActivitySelected}
            />
          )}
        </KTCard>
      </>
    )
  },
)

const TableActivitiesByScheduleWrapper = () => {
  const [showModalNew, setShowModalNew] = useState(false)
  const {currentTabItem} = useLimpieza()
  const [campamentoId, setCampamentoId] = useState('')
  const [asignados, setAsignados] = useState<any[]>([])

  const tableRef = useRef<TableActivitiesByScheduleRef>(null)

  const reloadTable = () => {
    tableRef.current?.reload()
  }

  const getCellphoneById = async () => {
    try {
      const {idToEdit} = currentTabItem
      if (idToEdit) {
        const {
          data: {data},
        } = await CLEANING_SCHEDULE_SERVICES.getByIdCleaningSchedule(idToEdit)
        if (data) {
          const {CampamentoId, Actividades} = data

          setCampamentoId(CampamentoId)
          setAsignados(Actividades || [])
        }
      }
    } catch (error) {}
  }

  useEffect(() => {
    if (currentTabItem.idToEdit) {
      getCellphoneById()
    }
  }, [currentTabItem.idToEdit])

  return (
    <>
      {currentTabItem.idToEdit && campamentoId && (
        <>
          <KTableProvider key='table'>
            <TableActivitiesBySchedule
              ref={tableRef}
              setShow={setShowModalNew}
              ServiceSchedulingId={currentTabItem.idToEdit}
              CampamentoId={campamentoId}
            />
          </KTableProvider>
          <KTableProvider key='modal'>
            <ModalAddActivities
              show={showModalNew}
              setShow={setShowModalNew}
              reloadTable={reloadTable}
              ServiceSchedulingId={currentTabItem.idToEdit}
              CampamentoId={campamentoId || ''}
              Asignados={asignados || []}
            />
          </KTableProvider>
        </>
      )}
    </>
  )
}
export {TableActivitiesByScheduleWrapper}
